<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900">
          Billing Portal
        </h1>
        <p class="mt-2 text-gray-600">
          Manage your subscription, payment methods, and billing information
        </p>
      </div>
      
      <!-- Tabs -->
      <div class="border-b border-gray-200 mb-8">
        <nav class="-mb-px flex space-x-8">
          <button
            v-for="tab in tabs"
            :key="tab.name"
            @click="currentTab = tab.name"
            :class="[
              currentTab === tab.name
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm'
            ]"
          >
            {{ tab.label }}
            <span v-if="tab.count" class="ml-2 bg-gray-100 text-gray-600 text-xs font-semibold px-2 py-0.5 rounded-full">
              {{ tab.count }}
            </span>
          </button>
        </nav>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div v-else class="space-y-8">
        <!-- Subscription Tab -->
        <SubscriptionManagement
          v-if="currentTab === 'subscription'"
          @error="handleError"
        />

        <!-- Payment Methods Tab -->
        <div v-else-if="currentTab === 'payment-methods'" class="space-y-6">
          <PaymentMethods />
        </div>
        
        <!-- Billing Info Tab -->
        <div v-else-if="currentTab === 'billing-info'" class="space-y-6">
          <BillingInformation />
        </div>
        
        <!-- Invoices Tab -->
        <div v-else-if="currentTab === 'invoices'" class="space-y-6">
          <InvoiceHistory />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import PaymentMethods from './PaymentMethods.vue';
import BillingInformation from './BillingInformation.vue';
import InvoiceHistory from './InvoiceHistory.vue';
import SubscriptionManagement from './SubscriptionManagement.vue';

export default {
  name: 'BillingPortal',
  props: {
    type: {
      type: String,
      default: 'subscription'
    }
  },
  components: {
    PaymentMethods,
    BillingInformation,
    InvoiceHistory,
    SubscriptionManagement
  },
  data() {
    return {
      currentTab: '',
      loading: false,
      error: null,
      stripeKey: window.stripeKey || '',
      tabs: [
        { name: 'subscription', label: 'Subscription' },
        { name: 'payment-methods', label: 'Payment Methods', count: 0 },
        { name: 'billing-info', label: 'Billing Information' },
        { name: 'invoices', label: 'Invoice History', count: 0 }
      ]
    };
  },
  watch: {
    type: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.tabs.some(tab => tab.name === newVal)) {
          this.currentTab = newVal;
        } else {
          this.currentTab = 'subscription';
        }
      }
    }
  },
  computed: {
    ...mapState('billing', [
      'paymentMethods',
      'invoices',
      'billingInfo',
    ])
  },
  async mounted() {
    try {
      this.loading = true;
      await Promise.all([
        this.fetchPaymentMethods(),
        this.fetchInvoices()
      ]);
      
      // Set tab counts
      this.tabs = this.tabs.map(tab => {
        if (tab.name === 'payment-methods') {
          return { ...tab, count: this.paymentMethods?.length || 0 };
        } else if (tab.name === 'invoices') {
          return { ...tab, count: this.invoices?.items?.length || 0 };
        }
        return tab;
      });
      this.$watch('currentTab', (newTab) => {
        window.location.hash = newTab;
      });
    } catch (error) {
      console.error('Error initializing billing portal:', error);
      this.error = 'Failed to load billing information. Please try again later.';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    ...mapActions('billing', [
      'fetchPaymentMethods',
      'fetchInvoices',
      'fetchBillingInfo',
    ]),
    
    handleError(error) {
      this.error = error;
      console.error('Billing error:', error);
    },
  }
};
</script>
